// ============================================
// IESProfileManager — 2D IES profile support for photometric light distribution
// ============================================
// IES (Illuminating Engineering Society) profiles define angular intensity
// distributions for realistic luminaire shapes. In 2D, we use simplified 1D
// profiles that modulate light intensity based on angle from the light source.

import type { IRenderContext } from '../core/IRenderContext';
import type { BufferHandle } from '../types';
import { NULL_HANDLE } from '../types';
import { graphicsConfig } from '../GraphicsConfig';

// ============================================================================
// IES Profile Resolution
// ============================================================================

/** Number of samples per IES profile (angular resolution). */
export const IES_PROFILE_RESOLUTION = 256;

/** Maximum number of IES profiles in the atlas. */
export const MAX_IES_PROFILES = 32;

// ============================================================================
// Built-in Profile Types
// ============================================================================

/**
 * Built-in IES profile presets.
 * Each preset generates a unique angular intensity distribution.
 */
export type IESPreset =
  | 'uniform'         // Fully omnidirectional (no IES effect)
  | 'narrow-spot'     // Tight focused beam (theatrical spotlight)
  | 'wide-spot'       // Wider focused beam
  | 'wall-washer'     // Asymmetric wash pattern for walls
  | 'chandelier'      // Decorative multi-lobe pattern
  | 'downlight'       // Typical recessed ceiling light
  | 'pendant'         // Hanging light with upward+downward emission
  | 'sconce'          // Wall-mounted fixture pattern
  | 'candle'          // Flickery flame-like distribution
  | 'projector'       // Sharp-edged projection beam
  | 'flood'           // Wide even flood
  | 'batwing';        // Batwing distribution (common in office lighting)

/**
 * IES profile definition with metadata.
 */
export interface IESProfile {
  /** Unique identifier / name. */
  name: string;
  /** Human-readable description. */
  description: string;
  /** Preset type (if built-in). */
  preset?: IESPreset;
  /** 1D intensity curve: values[i] = intensity at angle (i / resolution) * 2π. */
  values: Float32Array;
}

// ============================================================================
// Profile Generation Functions
// ============================================================================

/** Generate a uniform (omnidirectional) profile — baseline, no modulation. */
function generateUniform(): Float32Array {
  const data = new Float32Array(IES_PROFILE_RESOLUTION);
  data.fill(1.0);
  return data;
}

/** Generate a narrow spot profile — tight Gaussian beam. */
function generateNarrowSpot(): Float32Array {
  const data = new Float32Array(IES_PROFILE_RESOLUTION);
  const center = IES_PROFILE_RESOLUTION / 2; // 180° = "forward" for spot
  const sigma = IES_PROFILE_RESOLUTION * 0.05; // ~18° spread
  for (let i = 0; i < IES_PROFILE_RESOLUTION; i++) {
    const dist = Math.min(Math.abs(i - center), IES_PROFILE_RESOLUTION - Math.abs(i - center));
    data[i] = Math.exp(-(dist * dist) / (2 * sigma * sigma));
  }
  return data;
}

/** Generate a wide spot profile — broader Gaussian. */
function generateWideSpot(): Float32Array {
  const data = new Float32Array(IES_PROFILE_RESOLUTION);
  const center = IES_PROFILE_RESOLUTION / 2;
  const sigma = IES_PROFILE_RESOLUTION * 0.15; // ~54° spread
  for (let i = 0; i < IES_PROFILE_RESOLUTION; i++) {
    const dist = Math.min(Math.abs(i - center), IES_PROFILE_RESOLUTION - Math.abs(i - center));
    data[i] = Math.exp(-(dist * dist) / (2 * sigma * sigma));
  }
  return data;
}

/** Generate wall washer profile — asymmetric, stronger on one side. */
function generateWallWasher(): Float32Array {
  const data = new Float32Array(IES_PROFILE_RESOLUTION);
  const peak = IES_PROFILE_RESOLUTION * 0.6; // Off-center peak
  const sigma = IES_PROFILE_RESOLUTION * 0.2;
  for (let i = 0; i < IES_PROFILE_RESOLUTION; i++) {
    const dist = Math.min(Math.abs(i - peak), IES_PROFILE_RESOLUTION - Math.abs(i - peak));
    data[i] = Math.exp(-(dist * dist) / (2 * sigma * sigma));
  }
  return data;
}

/** Generate chandelier profile — decorative multi-lobe starfish pattern. */
function generateChandelier(): Float32Array {
  const data = new Float32Array(IES_PROFILE_RESOLUTION);
  const lobes = 6;
  for (let i = 0; i < IES_PROFILE_RESOLUTION; i++) {
    const angle = (i / IES_PROFILE_RESOLUTION) * Math.PI * 2;
    // Multi-lobe pattern with base level
    data[i] = 0.4 + 0.6 * Math.pow(Math.abs(Math.cos(angle * lobes * 0.5)), 2);
  }
  return data;
}

/** Generate downlight profile — strong downward, minimal upward. */
function generateDownlight(): Float32Array {
  const data = new Float32Array(IES_PROFILE_RESOLUTION);
  // Angle 0 = right, π/2 = down, π = left, 3π/2 = up in standard coords
  // "Down" in 2D game coords is typically +Y, which maps to angle = π/2 (index 64)
  const downIdx = IES_PROFILE_RESOLUTION / 4; // 90° = down
  const sigma = IES_PROFILE_RESOLUTION * 0.2;
  for (let i = 0; i < IES_PROFILE_RESOLUTION; i++) {
    const dist = Math.min(Math.abs(i - downIdx), IES_PROFILE_RESOLUTION - Math.abs(i - downIdx));
    data[i] = Math.exp(-(dist * dist) / (2 * sigma * sigma));
  }
  return data;
}

/** Generate pendant profile — emits both up and down. */
function generatePendant(): Float32Array {
  const data = new Float32Array(IES_PROFILE_RESOLUTION);
  const downIdx = IES_PROFILE_RESOLUTION / 4;
  const upIdx = (IES_PROFILE_RESOLUTION * 3) / 4;
  const sigma = IES_PROFILE_RESOLUTION * 0.12;
  for (let i = 0; i < IES_PROFILE_RESOLUTION; i++) {
    const distDown = Math.min(Math.abs(i - downIdx), IES_PROFILE_RESOLUTION - Math.abs(i - downIdx));
    const distUp = Math.min(Math.abs(i - upIdx), IES_PROFILE_RESOLUTION - Math.abs(i - upIdx));
    const down = Math.exp(-(distDown * distDown) / (2 * sigma * sigma));
    const up = 0.3 * Math.exp(-(distUp * distUp) / (2 * sigma * sigma));
    data[i] = Math.max(down, up);
  }
  return data;
}

/** Generate sconce profile — half-circle emission (wall-mounted). */
function generateSconce(): Float32Array {
  const data = new Float32Array(IES_PROFILE_RESOLUTION);
  // Emit from 0° to 180° (right hemisphere)
  for (let i = 0; i < IES_PROFILE_RESOLUTION; i++) {
    const angle = (i / IES_PROFILE_RESOLUTION) * Math.PI * 2;
    // Smooth cosine falloff in the forward hemisphere
    data[i] = Math.max(0, Math.cos(angle - Math.PI * 0.5));
  }
  return data;
}

/** Generate candle profile — flickery uneven distribution. */
function generateCandle(): Float32Array {
  const data = new Float32Array(IES_PROFILE_RESOLUTION);
  // Mostly upward emission with random-ish lobes
  const upIdx = (IES_PROFILE_RESOLUTION * 3) / 4;
  const sigma = IES_PROFILE_RESOLUTION * 0.3;
  for (let i = 0; i < IES_PROFILE_RESOLUTION; i++) {
    const dist = Math.min(Math.abs(i - upIdx), IES_PROFILE_RESOLUTION - Math.abs(i - upIdx));
    const base = Math.exp(-(dist * dist) / (2 * sigma * sigma));
    // Add subtle high-frequency variation
    const noise = 0.9 + 0.1 * Math.sin(i * 0.5) * Math.cos(i * 0.3);
    data[i] = base * noise;
  }
  return data;
}

/** Generate projector profile — sharp rectangular beam. */
function generateProjector(): Float32Array {
  const data = new Float32Array(IES_PROFILE_RESOLUTION);
  const center = IES_PROFILE_RESOLUTION / 2;
  const halfWidth = IES_PROFILE_RESOLUTION * 0.08; // ~30° total
  for (let i = 0; i < IES_PROFILE_RESOLUTION; i++) {
    const dist = Math.min(Math.abs(i - center), IES_PROFILE_RESOLUTION - Math.abs(i - center));
    // Sharp falloff using smoothstep
    const t = Math.max(0, 1 - dist / halfWidth);
    data[i] = t * t * (3 - 2 * t); // smoothstep
  }
  return data;
}

/** Generate flood profile — wide even distribution. */
function generateFlood(): Float32Array {
  const data = new Float32Array(IES_PROFILE_RESOLUTION);
  const center = IES_PROFILE_RESOLUTION / 2;
  // Very wide Gaussian — nearly uniform in forward hemisphere
  const sigma = IES_PROFILE_RESOLUTION * 0.4;
  for (let i = 0; i < IES_PROFILE_RESOLUTION; i++) {
    const dist = Math.min(Math.abs(i - center), IES_PROFILE_RESOLUTION - Math.abs(i - center));
    data[i] = 0.2 + 0.8 * Math.exp(-(dist * dist) / (2 * sigma * sigma));
  }
  return data;
}

/** Generate batwing profile — M-shaped distribution (common in office lights). */
function generateBatwing(): Float32Array {
  const data = new Float32Array(IES_PROFILE_RESOLUTION);
  const center = IES_PROFILE_RESOLUTION / 2;
  for (let i = 0; i < IES_PROFILE_RESOLUTION; i++) {
    const dist = Math.abs(i - center);
    const wrap = Math.min(dist, IES_PROFILE_RESOLUTION - dist);
    const norm = wrap / (IES_PROFILE_RESOLUTION / 4);
    // M-shape: peaks at ~30° off-axis, dip in center
    if (norm < 0.3) {
      data[i] = 0.6 + norm * 1.3; // ramp up
    } else if (norm < 0.6) {
      data[i] = 1.0; // plateau
    } else {
      data[i] = Math.max(0, 1.0 - (norm - 0.6) * 2.0); // falloff
    }
  }
  return data;
}

// ============================================================================
// Profile Factory
// ============================================================================

/** Generate an IES profile from a preset name. */
export function generateIESPreset(preset: IESPreset): Float32Array {
  switch (preset) {
    case 'uniform': return generateUniform();
    case 'narrow-spot': return generateNarrowSpot();
    case 'wide-spot': return generateWideSpot();
    case 'wall-washer': return generateWallWasher();
    case 'chandelier': return generateChandelier();
    case 'downlight': return generateDownlight();
    case 'pendant': return generatePendant();
    case 'sconce': return generateSconce();
    case 'candle': return generateCandle();
    case 'projector': return generateProjector();
    case 'flood': return generateFlood();
    case 'batwing': return generateBatwing();
    default: return generateUniform();
  }
}

/** Create an IESProfile object from a preset. */
export function createIESProfile(preset: IESPreset, name?: string): IESProfile {
  const descriptions: Record<IESPreset, string> = {
    'uniform': 'Fully omnidirectional (no IES modulation)',
    'narrow-spot': 'Tight focused theatrical spotlight beam',
    'wide-spot': 'Broader focused spotlight',
    'wall-washer': 'Asymmetric wash pattern for illuminating walls',
    'chandelier': 'Decorative multi-lobe starfish pattern',
    'downlight': 'Recessed ceiling downlight distribution',
    'pendant': 'Hanging pendant with up and down emission',
    'sconce': 'Wall-mounted half-circle emission',
    'candle': 'Flickery candle flame pattern',
    'projector': 'Sharp-edged projection beam',
    'flood': 'Wide even flood distribution',
    'batwing': 'M-shaped office lighting distribution',
  };
  return {
    name: name ?? preset,
    description: descriptions[preset],
    preset,
    values: generateIESPreset(preset),
  };
}

// ============================================================================
// IESProfileManager
// ============================================================================

/**
 * Manages IES profiles and uploads them to a GPU storage buffer.
 *
 * Usage:
 * ```ts
 * const mgr = new IESProfileManager(ctx);
 *
 * // Register profiles
 * const idx1 = mgr.addPreset('narrow-spot');
 * const idx2 = mgr.addPreset('downlight');
 * const idx3 = mgr.addProfile(customProfile);
 *
 * // Get profile index for a light
 * const profileIdx = mgr.getProfileIndex('narrow-spot');
 *
 * // Bind the buffer in your pipeline
 * mgr.iesBuffer; // bind to storage binding
 * ```
 */
export class IESProfileManager {
  private ctx: IRenderContext;

  // GPU storage buffer: flattened profiles [profile0_samples][profile1_samples]...
  private _iesBuffer: BufferHandle = NULL_HANDLE;

  // CPU staging data
  private _cpuData: Float32Array;

  // Profile registry: name → index
  private _profileMap = new Map<string, number>();
  private _profiles: IESProfile[] = [];
  private _profileCount = 0;
  private _dirty = false;

  constructor(ctx: IRenderContext) {
    this.ctx = ctx;

    // Allocate buffer for all profiles
    const totalSamples = IES_PROFILE_RESOLUTION * MAX_IES_PROFILES;
    this._cpuData = new Float32Array(totalSamples);

    // Initialize with uniform (index 0 = no IES effect)
    this.addPreset('uniform');

    // Create GPU buffer
    this._iesBuffer = ctx.createBuffer({
      size: totalSamples * 4, // f32 = 4 bytes
      usage: ['storage', 'copy-dst'],
      label: 'ies-profile-buffer',
    });

    this._dirty = true;
  }

  // ---------------------------------------------------------------------------
  // Accessors
  // ---------------------------------------------------------------------------

  /** GPU storage buffer containing IES profile data. */
  get iesBuffer(): BufferHandle { return this._iesBuffer; }

  /** Number of registered profiles. */
  get profileCount(): number { return this._profileCount; }

  /** Get all registered profiles. */
  get profiles(): readonly IESProfile[] { return this._profiles; }

  // ---------------------------------------------------------------------------
  // Profile Registration
  // ---------------------------------------------------------------------------

  /**
   * Add a built-in preset profile.
   * @returns The profile index (for use in light's iesProfileIndex).
   */
  addPreset(preset: IESPreset, name?: string): number {
    const profileName = name ?? preset;
    // Check if already registered
    const existing = this._profileMap.get(profileName);
    if (existing !== undefined) return existing;

    return this.addProfile(createIESProfile(preset, profileName));
  }

  /**
   * Add a custom IES profile.
   * @returns The profile index.
   */
  addProfile(profile: IESProfile): number {
    if (this._profileCount >= MAX_IES_PROFILES) {
      console.warn(`[IESProfileManager] Max profiles reached (${MAX_IES_PROFILES})`);
      return 0; // Fall back to uniform
    }

    const existing = this._profileMap.get(profile.name);
    if (existing !== undefined) return existing;

    const index = this._profileCount++;
    this._profileMap.set(profile.name, index);
    this._profiles.push(profile);

    // Copy profile data to CPU staging
    const offset = index * IES_PROFILE_RESOLUTION;
    this._cpuData.set(profile.values.subarray(0, IES_PROFILE_RESOLUTION), offset);
    this._dirty = true;

    return index;
  }

  /**
   * Get profile index by name.
   * @returns Profile index, or -1 if not found.
   */
  getProfileIndex(name: string): number {
    return this._profileMap.get(name) ?? -1;
  }

  /**
   * Get profile by name.
   */
  getProfile(name: string): IESProfile | undefined {
    const idx = this._profileMap.get(name);
    return idx !== undefined ? this._profiles[idx] : undefined;
  }

  // ---------------------------------------------------------------------------
  // GPU Upload
  // ---------------------------------------------------------------------------

  /**
   * Upload dirty profiles to GPU. Call once per frame if profiles changed.
   */
  upload(): void {
    if (!this._dirty) return;
    this._dirty = false;

    // Upload only used portion
    const usedSamples = this._profileCount * IES_PROFILE_RESOLUTION;
    this.ctx.updateBuffer(this._iesBuffer, this._cpuData.subarray(0, usedSamples));
  }

  // ---------------------------------------------------------------------------
  // Cleanup
  // ---------------------------------------------------------------------------

  /** Release GPU resources. */
  destroy(): void {
    if (this._iesBuffer !== NULL_HANDLE) {
      this.ctx.destroyBuffer(this._iesBuffer);
      this._iesBuffer = NULL_HANDLE;
    }
    this._profileMap.clear();
    this._profiles.length = 0;
    this._profileCount = 0;
  }
}

// ============================================================================
// Default Export
// ============================================================================

/** List of all built-in IES presets. */
export const IES_PRESETS: readonly IESPreset[] = [
  'uniform',
  'narrow-spot',
  'wide-spot',
  'wall-washer',
  'chandelier',
  'downlight',
  'pendant',
  'sconce',
  'candle',
  'projector',
  'flood',
  'batwing',
] as const;
